/**
 * Step — Name & link. Booth name + shareable slug (live availability) and the
 * session style (AI-proposed, flippable). Controls only: the booth preview
 * lives in the shell (Onboarding.tsx).
 */

import React, { useEffect, useRef } from 'react'
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa'
import { useOnboardingStore, slugify } from '../onboardingStore'
import { checkSlugAvailability } from '../onboardingApi'
import { useCopy } from '../regionCopy'
import { publicBoothHostLabel } from '../publicOrigin'

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const StepBasics: React.FC = () => {
  const spec = useOnboardingStore((s) => s.spec)
  const name = useOnboardingStore((s) => s.name)
  const slug = useOnboardingStore((s) => s.slug)
  const slugStatus = useOnboardingStore((s) => s.slugStatus)
  const captureMode = useOnboardingStore((s) => s.captureMode)
  const setName = useOnboardingStore((s) => s.setName)
  const setSlug = useOnboardingStore((s) => s.setSlug)
  const setSlugStatus = useOnboardingStore((s) => s.setSlugStatus)
  const setCaptureMode = useOnboardingStore((s) => s.setCaptureMode)
  const c = useCopy()

  // A slug already present on mount was claimed in the hero headline, so the
  // name field must not overwrite it — that was the visitor's explicit choice.
  const slugTouched = useRef(slug.length > 0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleName = (value: string): void => {
    setName(value)
    if (!slugTouched.current) setSlug(slugify(value))
  }
  const handleSlug = (value: string): void => {
    slugTouched.current = true
    setSlug(slugify(value))
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (slug.length < 3 || !SLUG_RE.test(slug)) {
      setSlugStatus(slug.length === 0 ? 'idle' : 'invalid')
      return
    }
    setSlugStatus('checking')
    debounceRef.current = setTimeout(async () => {
      const available = await checkSlugAvailability(slug)
      if (useOnboardingStore.getState().slug !== slug) return
      setSlugStatus(available ? 'available' : 'taken')
    }, 450)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [slug, setSlugStatus])

  return (
    <div className="flex h-full flex-col items-center overflow-y-auto soft-scrollbar px-6 py-6">
      <div className="flex w-full max-w-md flex-col gap-6">
        <section className="space-y-4">
          <header>
            <h2 className="text-xl font-semibold">{c.stepName}</h2>
          </header>
          <label className="block space-y-2">
            <span className="text-sm font-medium text-whiteout/80">{c.photoboothName}</span>
            <input
              value={name}
              onChange={(e) => handleName(e.target.value)}
              maxLength={60}
              className="w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-whiteout placeholder-whiteout/30 outline-none focus:border-blueSparkle"
            />
          </label>
          <label className="block space-y-2">
            <span className="text-sm font-medium text-whiteout/80">{c.boothLink}</span>
            <div className="flex items-center overflow-hidden rounded-xl border border-white/15 bg-white/[0.04] focus-within:border-blueSparkle">
              <span className="select-none whitespace-nowrap pl-4 text-whiteout/40">
                {publicBoothHostLabel()}/
              </span>
              <input
                value={slug}
                onChange={(e) => handleSlug(e.target.value)}
                maxLength={40}
                className="w-full bg-transparent py-3 pr-3 text-whiteout placeholder-whiteout/30 outline-none"
              />
              <span className="px-3">
                {slugStatus === 'checking' && (
                  <FaSpinner className="animate-spin text-whiteout/50" />
                )}
                {slugStatus === 'available' && <FaCheckCircle className="text-green-400" />}
                {(slugStatus === 'taken' || slugStatus === 'invalid') && (
                  <FaTimesCircle className="text-red-400" />
                )}
              </span>
            </div>
            <span className="block min-h-[1.25rem] text-xs">
              {slugStatus === 'available' && (
                <span className="text-green-400">{c.slugAvailable}</span>
              )}
              {slugStatus === 'taken' && <span className="text-red-400">{c.slugTaken}</span>}
              {slugStatus === 'invalid' && (
                <span className="text-red-400">{c.slugInvalid}</span>
              )}
            </span>
          </label>
        </section>

        <section className="space-y-3">
          <header>
            <h2 className="text-xl font-semibold">{c.sessionStyle}</h2>
            <p className="text-sm text-whiteout/50">
              {c.aiSuggested(
                spec?.captureMode === 'frame-based' ? c.frameMode : c.classicStrip
              )}
            </p>
          </header>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => setCaptureMode('standard')}
              className={`flex-1 rounded-2xl border p-4 text-left transition-colors ${
                captureMode !== 'frame-based'
                  ? 'border-blueSparkle bg-blueSparkle/10'
                  : 'border-white/10 bg-white/[0.03] hover:border-white/25'
              }`}
            >
              <div className="text-sm font-semibold">{c.classicStrip}</div>
              <div className="mt-1 text-xs text-whiteout/50">{c.classicStripSub}</div>
            </button>
            <button
              type="button"
              onClick={() => setCaptureMode('frame-based')}
              className={`flex-1 rounded-2xl border p-4 text-left transition-colors ${
                captureMode === 'frame-based'
                  ? 'border-blueSparkle bg-blueSparkle/10'
                  : 'border-white/10 bg-white/[0.03] hover:border-white/25'
              }`}
            >
              <div className="text-sm font-semibold">{c.frameMode}</div>
              <div className="mt-1 text-xs text-whiteout/50">{c.frameModeSub}</div>
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

export default StepBasics
